import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';

import { SettingsService } from 'app/modules/settings/settings.service';
import { AppSettings } from 'app/modules/shared/app.settings';
import { AutoFocusDirective } from 'app/modules/shared/directives/auto-focus.directive';
import type { Country } from 'app/modules/shared/models/country';
import { Language } from 'app/modules/shared/models/language';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { TabViewModule } from 'primeng/tabview';
import { AppIcons } from 'app/modules/shared/app.icons';
import { OnlyLettersDirective } from 'app/modules/shared/directives/only-letters.directive';
import { TrimSpacesProfileDirective } from 'app/modules/shared/directives/trim-spaces-for-profile.directive';
import { OnlyDigitsDirective } from 'app/modules/shared/directives/only-digits.directives';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MiFormComponent } from 'app/modules/shared/components/mi-form/mi-form.component';
import * as _ from 'lodash';
import { AttributeData } from '../../../../shared/models/attribute.models';
import { EntityList } from '../../../../shared/models/entity.models';

@Component({
  selector: 'app-settings-user-profile',
  standalone: true,
  imports: [TranslateModule, ButtonModule, DialogModule, ToastModule, InputTextModule, ReactiveFormsModule, FormsModule, TabViewModule, DropdownModule, FileUploadModule, OnlyLettersDirective, OnlyDigitsDirective, TrimSpacesProfileDirective, AccessProviderDirective, MiFormComponent],
  templateUrl: './settings-user-profile.component.html',
  styleUrl: './settings-user-profile.component.scss',
  providers: [AutoFocusDirective]
})
export class SettingsUserProfileComponent implements OnInit {
  @ViewChild('dropdown') dropdown!: Dropdown;
  @ViewChild(MiFormComponent) form: MiFormComponent;
  miIcons = AppIcons;
  profileForm: FormGroup;
  addBtnLabel = this.cs.getLabel('label_save');
  editLabel: string = this.cs.getLabel('settings.edit');
  deleteLabel: string = this.cs.getLabel('settings.delete');
  deleteHeaderMsg: string = this.cs.getLabel('settings.events.delete_header');
  deleteMsg: string = this.cs.getLabel('settings.events.delete_msg');
  userDetails: any;
  userId: number;
  countries: any[] | undefined;
  selectedCountry: any;
  userName: string;
  language: Language;
  country: Country;
  data: AttributeData;
  userProfileAttributeData;
  attributeIdForUserProfile;
  showUserForm: boolean = false;
  tableData: any = [];
  initialProfileFormValues: any;
  isEditMode: boolean = false;
  showMobileValidationError = false;
  defaultLoaderImg = AppSettings.DEFAULT_LOADER_IMAGE;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.BOTH,
    tableViewCode: AppSettings.VIEW_CODE.ADD_EDIT_VIEW,
    forTenantCode: this.configService.getLoggedInTenantCode(),
  };
  filterIcon = 'pi pi-search';
  saveUserProfileBtnLabel;
  previousBtnLabel: any = this.cs.getLabel('lbl_cancel');
  entityId: any;
  activeIndex = 0;
  timeZones: any[] = [];
  languages: any[] = [];
  attributeValues: any;
  showForm: boolean = false;
  generalData: any;
  attributeLabels = {};
  userProfileEntityId;
  userProfileEntityData;
  userGeneralData;
  userAttributeData;
  userProfileModuleName = AppSettings.ENTITY_TYPE.USER_DETAIL;
  profileAttributeData;
  userInfo;
  loginUserId;
  profileData;
  displayAttributes: any;
  attributeId;
  firstName;
  lastName;
  emailID;
  initialLastName;
  initialEmail;
  initialFirstName;
  initialTimeZone;
  timeZonesLoaded = false;
  languagesLoaded = false;

  constructor(private cs: CommonBindingDataService, private formBuilder: FormBuilder,
    private configService: ConfigService, private entityService: EntityService, private settingsService: SettingsService,
    private messageService: MessageService, private confirmationService: ConfirmationService, private cd: ChangeDetectorRef,
    private metaDataService: MetaDataService) {
  }

  ngOnInit() {
    this.userInfo = JSON.parse(localStorage.getItem(AppSettings.PROFILE_DATA));
    this.firstName = this.userInfo.firstName;
    this.lastName = this.userInfo.lastName;
    this.emailID = this.userInfo.email;
    this.loginUserId = this.userInfo.userId;
    this.getTimeZones();
    this.searchLanguages();
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.setUserProfileEntityData();
  }

  updateButtonLabel() {
    this.saveUserProfileBtnLabel = this.isEditMode
      ? this.cs.getLabel('settings.lbl_update_general_settings')
      : this.cs.getLabel('settings.lbl_save_general_settings');
  }

  setUserProfileEntityData() {
    this.userProfileEntityData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getLoggedInTenantCode(),
      entityCode: AppSettings.ENTITY_TYPE.USER_DETAIL
    }
  }

  getTimeZones() {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
    }
    this.entityService.getTimeZones(requestBody).subscribe({
      next: (result: any) => {
        result.forEach(element => {
          this.timeZones.push({
            labelKey: element.gmtOffset,
            labelValue: `${element.timezone} - (${element.gmtOffset}) - ${this.metaDataService.getTimezoneAbbreviation(element.timezone)}`,
            value: element.timezone
          })
        });
        this.timeZonesLoaded = true;
        this.checkAndCallGetAttributes();
      },
      error: (error) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
      }
    })
  }

  searchLanguages() {
    const requestBody = {
      limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT_TRIAL_REVIEW,
      offset: 0,
      searchStr: "",
      defaultSortColumn: "updatedAt",
      defaultSortType: "desc",
      eventCode: "",
    }
    this.entityService.getLanguages(requestBody).subscribe({
      next: (result: any) => {
        result.data.forEach(element => {
          this.languages.push({
            labelKey: element.langCode,
            labelValue: element.langName
          });
        });
        this.languagesLoaded = true;
        this.checkAndCallGetAttributes();
      },
      error: (error) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
      }
    })
  }

  onFilter(event) {
    if (event.filter) {
      this.filterIcon = 'pi pi-times'
    } else {
      this.filterIcon = 'pi pi-search';
    }
  }

  onFilterIconClick() {
    if (this.filterIcon.includes('times')) {
      this.dropdown.filterValue = null;
      this.filterIcon = 'pi pi-search';
    }
  }

  checkAndCallGetAttributes() {
    if (this.timeZonesLoaded && this.languagesLoaded) {
      this.getAttributesForUserProfile();
    }
  }

  getAttributesForUserProfile() {
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_TYPE.USER_DETAIL, AppSettings.VIEW_CODE.ADD_EDIT_VIEW).subscribe(res => {
      if (res) {
        this.attributeLabels = this.cs.getAttributeLabels(res);
        this.generalData = this.cs.getOrganizedAttribute(res);
        this.attributeId = this.cs.getAttributeId('user_id', this.generalData);
        this.searchEntity(this.attributeId);
        const tab = this.generalData.tabs.find(ele => ele.tabCode === 'user_profile');
        const userProfileField = tab.groups.find(ele => ele.code === 'user_profile');
        const languageField = userProfileField.fields.find(ele => ele.attributeCode === 'language');
        languageField.presetValues = this.languages;
        const timezoneField = userProfileField.fields.find(ele => ele.attributeCode === 'time_zone');
        if (timezoneField) {
          timezoneField.presetValues = this.timeZones;
        }

        this.profileAttributeData = _.cloneDeep(this.generalData);

        this.profileAttributeData.tabs[0].groups[0].fields = this.profileAttributeData.tabs[0].groups[0].fields.filter(field => field.attributeCode !== 'user_id');

        this.profileAttributeData.tabs = this.profileAttributeData.tabs.map(tab => ({
          ...tab,
          groups: tab.groups.map(group => ({
            ...group,
            fields: group.fields.map((field: any) => {
              if (field.attributeCode === 'first_name' || field.attributeCode === 'last_name' || field.attributeCode === 'email') {
                if (this.configService.isSponsorUser() || this.configService.isSiteUser()) {
                  field.disabled = true;
                  field.validation.required = false;
                }
              }
              return field;
            })
          }))
        }));
      }
      this.updateButtonLabel();
    });
  }

  getEntityDetails() {
    this.entityService.getSettingsEntity(this.entityId, AppSettings.ENTITY_TYPE.USER_DETAIL, AppSettings.VIEW_CODE.ADD_EDIT_VIEW).subscribe({
      next: (response: any) => {
        this.attributeValues = this.cs.getOrgAttributeValues(response);
        this.showForm = true;
      },
      error: (error) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
      }
    })
  }

  saveProfileSettings(event) {
    const userProfileData = event;
    if (this.entityId) {
      this.userProfileEntityId = this.entityId;
      this.saveProfileData(this.userProfileEntityData.entityCode, userProfileData, false);
    } else {
      this.entityService.createEntities(this.userProfileEntityData.entityCode, this.userProfileEntityData).subscribe({
        next: (res: any) => {
          this.userProfileEntityId = res.entityId;
          this.saveProfileData(this.userProfileEntityData.entityCode, userProfileData, true);
        },
        error: (error) => {
          this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
        }
      });
    }
  }

  saveProfileData(entityCode, userProfileData, isNewEntity) {
    this.userGeneralData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_TYPE.USER_DETAIL,
      countryCode: this.country[0].countryCode,
      languageCode: this.language[0].langCode,
      data: []
    };
    let timeZoneValue;
    for (const [key, value] of Object.entries(userProfileData)) {
      if (key === 'time_zone') {
        timeZoneValue = value;
      }
      const attributeValue = value;
      if (attributeValue) {
        this.userGeneralData.data.push({
          attributeCode: key,
          attributeValue
        });
      }
    }

    this.userGeneralData.data.push({
      attributeCode: 'user_id',
      attributeValue: this.loginUserId,
    });

    this.userGeneralData.data = this.cs.mapAttributeIds(this.userGeneralData.data, this.generalData.tabs);
    this.entityService.saveAttributeData(entityCode, this.userProfileEntityId, this.userGeneralData).subscribe({
      next: () => {
        this.metaDataService.userSpecificTimezoneSubject.next(timeZoneValue);
        if (isNewEntity) {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('settings.lbl_user_profile_saved_successfully') });
        } else {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('settings.lbl_user_profile_updated_successfully') });
        }
      },
      error: (error) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
      }
    });
  }

  onCancel() {
    const fields = this.form.formGroupFields as { [key: string]: any };

    if (fields.first_name?.dirty) {
      fields.first_name.setValue(this.initialFirstName);
    }

    if (fields.last_name?.dirty) {
      fields.last_name.setValue(this.initialLastName);
    }

    if (fields.email?.dirty) {
      fields.email.setValue(this.initialEmail);
    }

    if (fields.time_zone?.dirty) {
      fields.time_zone.setValue(this.initialTimeZone);
    }

  }

  searchEntity(attributeIdForUserProfile) {
    this.entityData.countryCode = this.country[0].countryCode;
    this.entityData.filters.push({
      attributeId: attributeIdForUserProfile,
      attributeValue: this.loginUserId
    });
    this.entityService.searchEntity(AppSettings.ENTITY_CODE.USER_DETAIL, this.entityData).subscribe((res: any) => {
      const localRes = {
        data: [
          {
            values: {
              last_name: this.lastName,
              first_name: this.firstName,
              email: this.emailID,
            }
          }
        ]
      }
      this.tableData = [];
      this.tableData = this.cs.getTableData(res.data.length > 0 ? res : localRes);
      this.entityId = this.tableData[0]?.id;
      this.showUserForm = true;
      this.attributeValues = this.tableData[0];
      this.initialFirstName = this.attributeValues?.first_name
      this.initialLastName = this.attributeValues?.last_name
      this.initialEmail = this.attributeValues?.email
      this.initialTimeZone = this.attributeValues?.time_zone;
      this.isEditMode = !!this.attributeValues && Object.keys(this.attributeValues).length > 0;
      this.updateButtonLabel();
      this.cd.detectChanges();
    })
  }

  onNextBtnClick() {
    const btn = document.getElementById('user');
    btn.click();
  }

}
